import { useRef, useEffect, useCallback, useState } from 'react';

const getDefaultWsUrl = () => {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}/chatbot-ws`;
};

const DEFAULT_WS_URL = process.env.REACT_APP_CHATBOT_WS_URL || getDefaultWsUrl();

const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_DELAY_MS = 30_000;

const useChatWebSocket = ({ sid, rid, cid, onMessage, onReconnect, enabled = true }) => {
    const socketRef = useRef(null);
    const pendingSendsRef = useRef([]);
    const onMessageRef = useRef(onMessage);
    const onReconnectRef = useRef(onReconnect);
    const cidRef = useRef(cid);
    const reconnectAttemptsRef = useRef(0);
    const reconnectTimerRef = useRef(null);
    const [isConnected, setIsConnected] = useState(false);
    const [reconnectTrigger, setReconnectTrigger] = useState(0);
    const [reconnectState, setReconnectState] = useState(null);

    useEffect(() => { onMessageRef.current = onMessage; });
    useEffect(() => { onReconnectRef.current = onReconnect; });
    useEffect(() => { cidRef.current = cid; }, [cid]);

    useEffect(() => {
        const handleOffline = () => {
            console.log('[WS] 📡 offline');
            clearTimeout(reconnectTimerRef.current);
            setReconnectState({ offline: true });
            const socket = socketRef.current;
            if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
                socket.close(1000, 'offline');
            }
        };

        const handleOnline = () => {
            console.log('[WS] 📡 online — reconnecting');
            reconnectAttemptsRef.current = 0;
            setReconnectTrigger(t => t + 1);
        };

        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnline);
        return () => {
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnline);
        };
    }, []);

    useEffect(() => {
        if (!enabled) return;

        const url = new URL(DEFAULT_WS_URL);
        if (sid) url.searchParams.set('id', sid);
        if (rid) url.searchParams.set('recruitment_id', rid);
        const usedCid = cidRef.current;
        if (usedCid) url.searchParams.set('conversation_id', usedCid);

        console.log('[WS] 🚀 connecting', url.toString());

        const socket = new WebSocket(url.toString());
        socketRef.current = socket;

        socket.onopen = () => {
            console.log('[WS] ✅ connected', new Date().toISOString());
            if (usedCid) onReconnectRef.current?.();
            reconnectAttemptsRef.current = 0;
            setReconnectState(null);
            setIsConnected(true);
            const queued = pendingSendsRef.current.splice(0);
            if (queued.length) console.log('[WS] 📦 flushing queued sends', queued.length);
            queued.forEach(payload => socket.send(JSON.stringify(payload)));
        };

        socket.onmessage = (event) => {
            console.log('[WS] 📥 message:', event.data);
            onMessageRef.current?.(event.data);
        };

        socket.onerror = (event) => {
            console.error('[WS] ❌ error', event);
            setIsConnected(false);
        };

        socket.onclose = (event) => {
            console.warn(
                '[WS] 🔌 closed',
                `code=${event.code}`,
                `reason=${event.reason || '(none)'}`,
                `wasClean=${event.wasClean}`,
                new Date().toISOString()
            );
            setIsConnected(false);
            const isCurrent = socketRef.current === socket;
            if (isCurrent) socketRef.current = null;

            if (!event.wasClean && isCurrent && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
                reconnectAttemptsRef.current += 1;
                const attempt = reconnectAttemptsRef.current;
                console.log(`[WS] 🔄 scheduling reconnect attempt ${attempt}/${MAX_RECONNECT_ATTEMPTS} in ${RECONNECT_DELAY_MS / 1000}s`);
                setReconnectState({ attempt });
                reconnectTimerRef.current = setTimeout(() => {
                    setReconnectTrigger(t => t + 1);
                }, RECONNECT_DELAY_MS);
            }
        };

        return () => {
            clearTimeout(reconnectTimerRef.current);
            if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
                console.log('[WS] 🧹 closing on unmount');
                socket.close(1000, 'component unmounted');
            }
        };
    }, [sid, rid, enabled, reconnectTrigger]);

    const send = useCallback((payload) => {
        const socket = socketRef.current;
        if (!socket || socket.readyState === WebSocket.CLOSED || socket.readyState === WebSocket.CLOSING) {
            console.log('[WS] 📦 queued (offline)', payload);
            pendingSendsRef.current.push(payload);
            return false;
        }
        if (socket.readyState === WebSocket.OPEN) {
            console.log('[WS] 📤 send', payload);
            socket.send(JSON.stringify(payload));
            return true;
        }
        if (socket.readyState === WebSocket.CONNECTING) {
            console.log('[WS] 📦 queued (connecting)', payload);
            pendingSendsRef.current.push(payload);
            return true;
        }
        console.warn('[WS] send() failed — readyState=', socket.readyState);
        return false;
    }, []);

    return { send, isConnected, reconnectState };
};

export default useChatWebSocket;
